import type {
  Answer,
  FieldDefinition,
  FieldId,
  ValidationIssue,
} from '@inside-us-aos/domain';
import { validateAnswerValue } from '@inside-us-aos/domain';
import { resolveVisibleFieldIds } from '../engine/visibility';

/**
 * Per-field validation runner.
 *
 * Composes the domain's `validateAnswerValue` (which since Phase 0.5c
 * covers `required`, `type_mismatch`, numeric `min_value` / `max_value`,
 * and the string-shape rules `min_length` / `max_length` / `pattern`)
 * with the Slice 2 visibility resolver.
 *
 * Issues are emitted only for **visible** fields. An invisible field
 * cannot be satisfied, so reporting required-but-missing on it would
 * be a UX bug. This matches the visibility / required composition
 * established in Slice 2.
 *
 * Output ordering: stable by catalog order, then by issue insertion
 * order within a field. Slice 4 will fold one-of-group issues into
 * the same pipeline; until then this returns per-field issues only.
 */

/**
 * Validates a single field given the current answer set. Returns
 * an empty array when the field is invisible (no validation runs
 * against hidden fields). Otherwise returns whatever the domain
 * validator produces.
 */
export const validateField = (
  field: FieldDefinition,
  answer: Answer | undefined,
  answers: readonly Answer[],
  visibleFieldIds: ReadonlySet<FieldId>,
): ValidationIssue[] => {
  if (!visibleFieldIds.has(field.id)) return [];
  return validateAnswerValue(field, answer?.value, answers);
};

export interface ValidateAllAnswersResult {
  issues: readonly ValidationIssue[];
  /**
   * Convenience index for UI consumers that need "show issues for
   * field X" without re-scanning the issue array per render.
   */
  issuesByFieldId: ReadonlyMap<FieldId, readonly ValidationIssue[]>;
}

/**
 * Batch validation runner. Walks the catalog, validates every visible
 * field against the current answers, and returns a flat issue list
 * plus a per-field index. The Slice 2 visibility resolver is invoked
 * once per call; callers that already have a visibility set should
 * use `validateField` directly to avoid the second pass.
 */
export const validateAllAnswers = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ValidateAllAnswersResult => {
  const visibleFieldIds = resolveVisibleFieldIds(catalog, answers);
  const answerByFieldId = new Map(
    answers.map(answer => [answer.fieldId, answer]),
  );

  const issues: ValidationIssue[] = [];
  const issuesByFieldId = new Map<FieldId, ValidationIssue[]>();

  for (const field of catalog) {
    const fieldIssues = validateField(
      field,
      answerByFieldId.get(field.id),
      answers,
      visibleFieldIds,
    );
    if (fieldIssues.length === 0) continue;
    issues.push(...fieldIssues);
    issuesByFieldId.set(field.id, fieldIssues);
  }

  return { issues, issuesByFieldId };
};
